import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import styled from "styled-components";

import Header from "../components/Header/Header";
import Sidebar from "../components/Sidebar/Sidebar";
import { Backdrop } from "../components/Sidebar/SidebarStyle";
import theme from "../theme";

const BREAKPOINT = 820;

const Shell = styled.div`
  min-height: 100vh;
  background: ${theme.color.bg};
`;

const Main = styled.div`
  min-height: 100vh;
  margin-left: ${({ $offset }) => $offset};
  transition: margin-left 0.2s ease;

  @media (max-width: ${theme.layout.breakpoint}) {
    margin-left: 0;
  }
`;

const Content = styled.main`
  max-width: ${theme.layout.content};
  margin: 0 auto;
  padding: 28px 32px 56px;

  @media (max-width: ${theme.layout.breakpoint}) {
    padding: 20px 16px 40px;
  }
`;

const DefaultLayout = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < BREAKPOINT);
  // On desktop the sidebar is always mounted and this collapses it to icons.
  // On mobile it slides in and out instead.
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();
  const sidebarRef = useRef();

  const pageWords =
    location.pathname === "/"
      ? "Overview"
      : location.pathname.replace(/[^a-zA-Z]/g, " ");
  const headerText = pageWords.replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
    letter.toUpperCase()
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen((open) => !open);
    } else {
      setCollapsed((value) => !value);
    }
  }, [isMobile]);

  const showSidebar = !isMobile || mobileOpen;
  const offset = collapsed
    ? theme.layout.sidebarCollapsed
    : theme.layout.sidebar;

  return (
    <Shell>
      {showSidebar && (
        <Sidebar
          forwardRef={sidebarRef}
          collapsed={!isMobile && collapsed}
          onNavigate={() => setMobileOpen(false)}
        />
      )}
      {isMobile && mobileOpen && (
        <Backdrop onClick={() => setMobileOpen(false)} />
      )}

      <Main $offset={offset}>
        <Header
          title={headerText}
          onToggleSidebar={toggleSidebar}
          collapsed={collapsed}
        />
        <Content>
          <Outlet />
        </Content>
      </Main>
    </Shell>
  );
};

export default DefaultLayout;
